'use client'

import Link from 'next/link'
import { useTheme } from '@/contexts/ThemeContext'
import { ArrowLeft, AlertTriangle, Ear, CheckCircle, Info } from 'lucide-react'

export default function EqualizationPage() {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  return (
    <div className={`min-h-screen py-4 xxs:py-3 px-3 xxs:px-2 ${
      isDark ? 'bg-gray-900' : 'bg-gradient-to-b from-blue-50 to-white'
    }`}>
      <div className="max-w-3xl mx-auto">
        <Link
          href="/safety"
          className={`inline-flex items-center gap-1 mb-4 xxs:mb-3 text-sm xxs:text-xs ${
            isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4 xxs:w-3.5 xxs:h-3.5" />
          안전 가이드 목록
        </Link>

        <article className={`rounded-xl shadow-lg overflow-hidden ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
          {/* 헤더 */}
          <div className={`p-6 xxs:p-4 ${isDark ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
            <div className="flex items-center gap-3 xxs:gap-2">
              <div className="p-2 xxs:p-1.5 rounded-lg bg-blue-500">
                <Ear className="w-6 h-6 xxs:w-5 xxs:h-5 text-white" />
              </div>
              <h1 className={`text-2xl xxs:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                이퀄라이징 기법
              </h1>
            </div>
            <p className={`mt-3 xxs:mt-2 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              귀 압력 평형을 맞추는 이퀄라이징 기법을 마스터하세요.
            </p>
          </div>

          <div className="p-6 xxs:p-4 space-y-6 xxs:space-y-4">
            {/* 이퀄라이징이란? */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-3 xxs:mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                이퀄라이징이란?
              </h2>
              <p className={`text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                수심이 깊어질수록 외부 수압이 증가하여 귀 내부의 압력과 차이가 생깁니다.
                이퀄라이징은 이 압력 차를 조절하여 귀의 통증과 손상을 방지하는 기술입니다.
              </p>
            </div>

            {/* 기법들 */}
            <div className="space-y-4 xxs:space-y-3">
              {/* 프렌젤 */}
              <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                  1. 프렌젤 기법 (Frenzel)
                </h3>
                <p className={`text-sm xxs:text-xs mb-3 xxs:mb-2 ${isDark ? 'text-blue-200/80' : 'text-blue-700'}`}>
                  프리다이빙에서 가장 많이 사용하는 기법
                </p>
                <ul className={`space-y-1 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-500" /> 코를 막고 혀를 이용해 압력 전달</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-500" /> 'K' 또는 'T' 소리를 내듯이 혀 사용</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-500" /> 적은 공기로 효과적</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-500" /> 깊은 수심에서도 가능</li>
                </ul>
              </div>

              {/* 발살바 */}
              <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-purple-900/20' : 'bg-purple-50'}`}>
                <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-purple-300' : 'text-purple-800'}`}>
                  2. 발살바 기법 (Valsalva)
                </h3>
                <p className={`text-sm xxs:text-xs mb-3 xxs:mb-2 ${isDark ? 'text-purple-200/80' : 'text-purple-700'}`}>
                  초보자가 배우기 쉬운 기법
                </p>
                <ul className={`space-y-1 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-purple-500" /> 코를 막고 부드럽게 코로 바람 불기</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-purple-500" /> 복부 압력을 이용</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-purple-500" /> 얕은 수심에서 효과적</li>
                  <li className="flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5 text-orange-500" /> 너무 세게 하면 위험</li>
                </ul>
              </div>

              {/* 마우스필 */}
              <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-orange-900/20' : 'bg-orange-50'}`}>
                <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-orange-300' : 'text-orange-800'}`}>
                  3. 마우스필 (Mouthfill)
                </h3>
                <p className={`text-sm xxs:text-xs mb-3 xxs:mb-2 ${isDark ? 'text-orange-200/80' : 'text-orange-700'}`}>
                  고급 기술 - 깊은 수심용
                </p>
                <ul className={`space-y-1 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-orange-500" /> 입 안에 공기를 저장</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-orange-500" /> 프렌젤과 조합하여 사용</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-orange-500" /> 20m 이상 깊은 수심에서 필수</li>
                  <li className="flex items-center gap-2"><Info className="w-3.5 h-3.5 text-blue-500" /> 전문 강사의 지도 필요</li>
                </ul>
              </div>
            </div>

            {/* 주의사항 */}
            <div className={`p-4 xxs:p-3 rounded-xl border-l-4 border-red-500 ${
              isDark ? 'bg-red-900/20' : 'bg-red-50'
            }`}>
              <div className="flex items-start gap-3 xxs:gap-2">
                <AlertTriangle className={`w-5 h-5 xxs:w-4 xxs:h-4 flex-shrink-0 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                <div>
                  <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-red-300' : 'text-red-900'}`}>
                    주의사항
                  </h3>
                  <ul className={`space-y-1.5 text-sm xxs:text-xs ${isDark ? 'text-red-200' : 'text-red-800'}`}>
                    <li>• 이퀄라이징이 안 되면 절대 무리하지 마세요</li>
                    <li>• 즉시 상승하여 다시 시도하세요</li>
                    <li>• 강제로 하면 고막 손상 위험</li>
                    <li>• 감기나 중이염이 있으면 다이빙 금지</li>
                    <li>• 통증이 있으면 즉시 중단</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 연습 방법 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                연습 방법
              </h2>
              <div className="space-y-2 xxs:space-y-1.5">
                {[
                  { step: '1단계', title: '육상 연습', desc: '거울을 보며 프렌젤 동작 익히기' },
                  { step: '2단계', title: '얕은 수심', desc: '1-3m에서 천천히 연습' },
                  { step: '3단계', title: '점진적 증가', desc: '편안해지면 조금씩 깊이 증가' },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className={`p-3 xxs:p-2 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}
                  >
                    <span className={`text-xs xxs:text-[10px] font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                      {item.step}
                    </span>
                    <p className={`font-semibold text-sm xxs:text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {item.title}
                    </p>
                    <p className={`text-xs xxs:text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  )
}
